import { type ReactNode, useEffect, useRef, useState, useCallback } from 'react';
import { Music, VolumeOff } from 'lucide-react';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { ParallaxBackground } from './ParallaxBackground';

interface PortalSceneProps {
  children: ReactNode;
  /** Content alignment: 'bottom' for login, 'center' for dashboard */
  align?: 'bottom' | 'center';
}

/**
 * Full-viewport portal scene with image background and ambient audio
 * Orchestrated reveal: mid → particles → background
 */
export function PortalScene({ children, align = 'bottom' }: PortalSceneProps) {
  const reducedMotion = useReducedMotion();
  const [showParticles, setShowParticles] = useState(false);

  // Called by ParallaxBackground when mid layer is visible
  const handleMidLayerReady = useCallback(() => {
    setShowParticles(true);
  }, []);

  const alignmentClasses =
    align === 'center' ? 'justify-start pt-8 pb-8 overflow-y-auto' : 'justify-end pb-16';

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-void">
      {/* Layered parallax background with orchestrated reveal */}
      <ParallaxBackground reducedMotion={reducedMotion} onMidLayerReady={handleMidLayerReady} />

      {/* Particle motes (dust floating) - fade in after mid layer */}
      <ParticleMotes reducedMotion={reducedMotion} visible={showParticles} />

      {/* Content overlay */}
      <div
        className={`relative z-10 min-h-screen flex flex-col items-center px-3 sm:px-4 ${alignmentClasses}`}
      >
        {children}
      </div>

      {/* Soft black vignette - overlays everything */}
      <div className="pointer-events-none absolute inset-0 z-20 bg-[radial-gradient(ellipse_at_center,transparent_20%,rgba(0,0,0,0.5)_100%)]" />

      {/* Ambient audio player */}
      <AmbientAudio reducedMotion={reducedMotion} />
    </div>
  );
}

/**
 * Floating particle motes (dust) with depth and wandering motion
 * Fades in when visible prop becomes true
 */
function ParticleMotes({ reducedMotion, visible }: { reducedMotion: boolean; visible: boolean }) {
  if (reducedMotion) return null;

  // Create particles with varying depths (layers)
  const particles = Array.from({ length: 30 }, (_, i) => {
    const depth = Math.random(); // 0 = far (small, slow), 1 = near (large, fast)
    const baseSize = depth > 0.7 ? 1.5 : depth > 0.4 ? 1 : depth > 0.2 ? 0.6 : 0.3;
    const baseDuration = 28 - depth * 14; // Far particles move slower

    return {
      id: i,
      left: `${5 + Math.random() * 90}%`,
      delay: `${Math.random() * 25}s`,
      duration: `${baseDuration + Math.random() * 10}s`,
      size: baseSize,
      opacity: 0.3 + depth * 0.3,
      // Pick one of several animation variants for variety
      animClass:
        i % 3 === 0
          ? 'animate-particle-drift-left'
          : i % 3 === 1
            ? 'animate-particle-drift-right'
            : 'animate-particle-drift-wiggle',
      // All particles blurry like floating dust specs, more blur for distant ones
      blur: depth < 0.3 ? 5 : depth < 0.6 ? 3 : 1.5,
    };
  });

  return (
    <div
      className={`absolute inset-0 overflow-hidden pointer-events-none z-[5] transition-opacity duration-1000 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {particles.map((p) => (
        <div
          key={p.id}
          className={`absolute bottom-0 rounded-full bg-parchment ${p.animClass}`}
          style={{
            left: p.left,
            width: `${p.size * 4}px`,
            height: `${p.size * 4}px`,
            animationDelay: p.delay,
            animationDuration: p.duration,
            opacity: p.opacity,
            filter: `blur(${p.blur}px)`,
          }}
        />
      ))}
    </div>
  );
}

const AUDIO_MUTED_KEY = 'loremail-audio-muted';

/**
 * Ambient audio player with mute toggle
 * Opt-in only; remembers preference
 */
function AmbientAudio({ reducedMotion }: { reducedMotion: boolean }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isMuted, setIsMuted] = useState(() => {
    if (typeof window === 'undefined') return false;
    const stored = localStorage.getItem(AUDIO_MUTED_KEY);
    return stored === null ? true : stored === 'true';
  });

  // Set up audio on mount
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || reducedMotion) return;

    audio.volume = 0.25;
    audio.loop = true;
    audio.muted = isMuted;
  }, [reducedMotion, isMuted]);

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;

    const newMuted = !isMuted;
    setIsMuted(newMuted);
    audio.muted = newMuted;
    localStorage.setItem(AUDIO_MUTED_KEY, String(newMuted));

    if (newMuted) {
      audio.pause();
      return;
    }

    // If unmuting and not playing yet, try to play
    if (audio.paused) {
      audio
        .play()
        .catch(() => {
          // User gesture required; keep muted state off so they can retry
        });
    }
  };

  if (reducedMotion) return null;

  return (
    <>
      <audio ref={audioRef} src="/assets/portal-ambient.mp3" preload="auto" />

      {/* Mute toggle button */}
      <button
        onClick={toggleMute}
        className="fixed bottom-4 right-4 z-50 p-3 rounded-full bg-void/60 border border-stone/40 text-parchment/70 hover:text-parchment hover:bg-void/80 transition-all duration-300 backdrop-blur-sm"
        aria-label={isMuted ? 'Unmute ambient audio' : 'Mute ambient audio'}
        title={isMuted ? 'Play ambient sounds' : 'Mute ambient sounds'}
      >
        {isMuted ? <VolumeOff size={20} /> : <Music size={20} />}
      </button>
    </>
  );
}
